import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableBodyProps,
  TableCell,
  TableContainer,
  TableHead,
  TableHeadProps,
  TableProps,
  TableRow,
  Typography,
} from '@mui/material';
import { type JSX } from 'react';

import { colors } from 'src/global/styles/colors';

interface DataTableProps {
  renderHeader: () => JSX.Element;
  renderBody: () => JSX.Element;
  tableProps?: TableProps;
  tableHeadProps?: TableHeadProps;
  tableBodyProps?: TableBodyProps;
  isLoading?: boolean;
  skeletonRowCount: number;
  isEmpty: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

const DataTable = (props: DataTableProps) => {
  const {
    renderHeader,
    renderBody,
    tableProps,
    tableHeadProps,
    tableBodyProps,
    isLoading = false,
    skeletonRowCount,
    isEmpty,
    emptyStateTitle = 'No data to display',
    emptyStateDescription = 'There are no items to show at this time.',
  } = props;

  return (
    <>
      <TableContainer>
        <Table
          sx={{
            backgroundColor: 'transparent',
            borderCollapse: 'collapse',
            '& td': {
              border: `1px solid ${colors.neutral015}`,
              color: 'white',
              textAlign: 'left',
            },
            '& th': {
              border: `1px solid ${colors.neutral015}`,
              textAlign: 'left',
              color: colors.neutral90,
            },
            ...tableProps?.sx,
          }}
          {...tableProps}>
          <TableHead {...tableHeadProps}>{renderHeader()}</TableHead>
          <TableBody {...tableBodyProps}>
            {isLoading
              ? Array.from({ length: skeletonRowCount }).map((_, index) => (
                  <TableRow key={index} sx={{ width: '100%' }}>
                    {Array.from({ length: skeletonRowCount }).map((__, colIndex) => (
                      <TableCell key={colIndex}>
                        <Skeleton
                          height={24}
                          sx={{
                            backgroundColor: colors.neutral015,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : renderBody()}
          </TableBody>
        </Table>
      </TableContainer>
      {isEmpty ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            width: '100%',
            border: `1px solid ${colors.neutral015}`,
            borderTop: 'none',
            padding: '100px 0',
          }}>
          <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}>
            {emptyStateTitle}
          </Typography>
          <Typography
            whiteSpace={'pre-line'}
            sx={{ color: colors.neutral90, fontSize: '14px', fontWeight: 400, lineHeight: '20px', marginTop: '8px' }}>
            {emptyStateDescription}
          </Typography>
        </Box>
      ) : null}
    </>
  );
};

export { DataTable };
export type { DataTableProps };
